import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { TourProps } from 'antd'
import {
  Alert,
  Button,
  Checkbox,
  List,
  message,
} from 'antd'
import { ArrowRightOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { getAuth } from 'firebase/auth'
import LoadingCard from './components/LoadingCard'
import AppPageHeader from './components/AppPageHeader'

import { useLanguageSurvey } from './hooks/useLanguageSurvey'
import { getAllLanguages, getLanguageDisplayName } from './LanguageConfig'
import { resolveFrontendApiBase } from './utils/apiBase'
import AppTour, { renderAppTourPanel } from './tours/AppTour'
import { tourTarget, useGuidedTour } from './tours/useGuidedTour'
import './LanguageSurvey.css'

const API_BASE = resolveFrontendApiBase()

const getCurrentUserId = () => {
  try {
    return getAuth().currentUser?.uid
  } catch {
    return undefined
  }
}

const LanguageSurveyLanding: React.FC = () => {
  const navigate = useNavigate()
  const { loading, saving, response, error, saveSelection } = useLanguageSurvey()
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([])
  const tourUserId = getCurrentUserId()

  const allLanguages = useMemo(() => getAllLanguages(true), [])

  useEffect(() => {
    if (response?.selectedLanguages) {
      setSelectedLanguages(response.selectedLanguages)
    }
  }, [response?.selectedLanguages])

  const handleToggleLanguage = (language: string) => {
    setSelectedLanguages((prev) =>
      prev.includes(language)
        ? prev.filter((value) => value !== language)
        : [...prev, language]
    )
  }

  const handleContinue = async () => {
    if (selectedLanguages.length === 0) {
      message.warning('Please choose at least one language to continue.')
      return
    }

    try {
      await saveSelection(selectedLanguages)
      message.success('Survey languages saved. Let’s get started!')
      navigate('/language-survey/start')
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      message.error(detail || 'Unable to save languages. Please try again.')
    }
  }

  const handleResume = () => {
    navigate('/language-survey/start')
  }
  const surveyLandingTour = useGuidedTour({
    userId: tourUserId,
    apiBase: API_BASE,
    flag: 'languageSurveyLandingTourSeen',
    enabled: true,
    autoStart: true,
    ready: !loading,
  })
  const surveyLandingTourSteps = useMemo<TourProps['steps']>(() => ([
    {
      title: 'Choose survey languages',
      description: renderAppTourPanel('Select the translation languages you want to report results for.'),
      target: tourTarget('language-survey-selection'),
    },
    {
      title: 'Track readiness',
      description: renderAppTourPanel('The selected count and ready state show whether the survey can start.'),
      target: tourTarget('language-survey-selection-status', 'language-survey-selection'),
    },
    {
      title: 'Continue or resume',
      description: renderAppTourPanel('Continue saves your language list. Resume reopens an existing survey response.'),
      target: tourTarget('language-survey-actions'),
    },
  ]), [loading])

  return (
    <main className="language-survey-shell">
      <div className="language-survey-inner">
        <AppPageHeader
          kicker="Language survey"
          title="Language Success Survey"
          description="Select the translation languages you want to include, then answer a short set of questions for each one."
          metrics={
            <span className={`language-survey-pill ${selectedLanguages.length > 0 ? 'is-ready' : 'is-needed'}`}>
              {selectedLanguages.length} selected
            </span>
          }
          actions={
            <Button
              type="primary"
              className="app-primary-cta"
              icon={<ArrowRightOutlined />}
              loading={saving}
              disabled={loading || saving || selectedLanguages.length === 0}
              onClick={handleContinue}
            >
              Continue
            </Button>
          }
          secondaryActions={
            <>
              <Button onClick={handleResume} disabled={loading || saving || !response}>
                Resume
              </Button>
              <Button icon={<QuestionCircleOutlined />} onClick={surveyLandingTour.start} data-tour-id="language-survey-landing-tour-trigger" className="app-tour-trigger">
                Take tour
              </Button>
            </>
          }
          titleId="language-survey-title"
        />

        <section className="language-survey-workspace" aria-labelledby="language-survey-selection-title">
          <section className="language-survey-section" data-tour-id="language-survey-selection">
            <div className="language-survey-section-header">
              <div>
                <div className="language-survey-kicker">Setup</div>
                <h2 id="language-survey-selection-title">Choose languages</h2>
                <p>You can update this list later. The survey starts with general questions, then walks through each selected language.</p>
              </div>
              <span className={`language-survey-pill ${selectedLanguages.length > 0 ? 'is-ready' : 'is-needed'}`} data-tour-id="language-survey-selection-status">
                {selectedLanguages.length > 0 ? 'Ready' : 'Needed'}
              </span>
            </div>

          {error && (
            <Alert
              type="error"
              message="We couldn't load your survey data. Please refresh or try again later."
            />
          )}

          {loading ? (
            <div className="language-survey-loading">
              <LoadingCard label="Preparing your survey..." />
            </div>
          ) : (
            <div className="language-survey-stack">
                <List
                  className="language-survey-language-list"
                  dataSource={allLanguages}
                  renderItem={(language) => {
                    const checked = selectedLanguages.includes(language)
                    return (
                      <List.Item
                        key={language}
                        onClick={() => handleToggleLanguage(language)}
                        className={`language-survey-language-row ${checked ? 'is-selected' : ''}`}
                      >
                        <span className="language-survey-language-control">
                          <Checkbox
                            checked={checked}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => handleToggleLanguage(language)}
                          />
                          <span>{getLanguageDisplayName(language)}</span>
                        </span>
                      </List.Item>
                    )
                  }}
                />

              <Alert
                type="info"
                showIcon
                message="General questions appear first, then shared language questions for each selection, followed by any language-specific questions."
              />

              <div className="language-survey-actions" data-tour-id="language-survey-actions">
                <Button onClick={handleResume} disabled={loading || saving || !response}>
                  Resume survey
                </Button>
                <Button
                  type="primary"
                  className="app-primary-cta"
                  icon={<ArrowRightOutlined />}
                  loading={saving}
                  disabled={loading || saving || selectedLanguages.length === 0}
                  onClick={handleContinue}
                >
                  Continue to survey
                </Button>
              </div>
            </div>
          )}
          </section>
        </section>
        <AppTour
          open={surveyLandingTour.open}
          steps={surveyLandingTourSteps}
          current={surveyLandingTour.current}
          onChange={surveyLandingTour.setCurrent}
          onClose={surveyLandingTour.close}
          onFinish={surveyLandingTour.close}
        />
      </div>
    </main>
  )
}

export default LanguageSurveyLanding
